import { useState } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { expValEquals } from '@confluence/feature-experiments';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import type {
	usePendingRequestExistsQuery as usePendingRequestExistsQueryType,
	usePendingRequestExistsQueryVariables,
} from './__types__/usePendingRequestExistsQuery.ts';
const UsePendingRequestExistsQuery = graphql`
	query usePendingRequestExistsQuery(
		$accessRequestedAaid: String!
		$cloudId: ID!
		$contentId: ID!
	) {
		confluence_pendingRequestExists(
			accessRequestedAaid: $accessRequestedAaid
			cloudId: $cloudId
			contentId: $contentId
		) {
			isPendingAccessRequestExists
		}
	}
`;

export const usePendingRequestExists = ({
	contentId,
	preferPreloadedData = false,
}: {
	contentId: string | null | undefined;
	/**
	 * When true, read the answer from the store first (`cache-first`) so an SSR-preloaded result is
	 * consumed instead of always refetching. Only the SSR-preloaded restricted-page path opts in;
	 * other callers keep the default `network-only` freshness.
	 */
	preferPreloadedData?: boolean;
}): {
	pendingRequestExists: boolean;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
	isLoadingForFirstTime: boolean;
	isPendingRequestResolvedForSSR: boolean;
} => {
	const { userId, cloudId } = useSessionData();

	const shouldSkip =
		!userId ||
		!cloudId ||
		!contentId ||
		!expValEquals('confluence_frontend_existing_access_request_checks', 'cohort', 'experiment');

	const [isLoadingForFirstTime, setIsLoadingForFirstTime] = useState(!shouldSkip);

	const { data, error, loading } = useQueryInterop<
		usePendingRequestExistsQueryType,
		usePendingRequestExistsQueryVariables
	>(UsePendingRequestExistsQuery, {
		variables: {
			cloudId,
			accessRequestedAaid: userId ?? '',
			contentId: contentId ?? '',
		},
		skip: shouldSkip,
		fetchPolicy: preferPreloadedData ? 'cache-first' : 'network-only',
		onCompleted: () => {
			setIsLoadingForFirstTime(false);
		},
	});

	// SSR-safe resolution signal, for consumers that render on the server. `true` as soon as the
	// answer is available in the store on the current render — including the server render, when the
	// result was SSR-preloaded and read via `cache-first`. Unlike `isLoadingForFirstTime`, this lets an
	// SSR consumer render the resolved copy directly into the server HTML.
	const isPendingRequestResolvedForSSR = shouldSkip || data !== undefined;

	const pendingRequestExists = Boolean(
		data?.confluence_pendingRequestExists?.isPendingAccessRequestExists,
	);

	return {
		pendingRequestExists,
		loading,
		error,
		isLoadingForFirstTime,
		isPendingRequestResolvedForSSR,
	};
};
